/**
 * Hook for detecting when a proposal card scrolls into the viewport.
 *
 * Uses IntersectionObserver with a 200px rootMargin so data fetches
 * begin slightly before the card becomes visible, avoiding a loading
 * flash on scroll.
 *
 * Based on the pattern in
 * `src/lib/crm/record/timeline/useInfiniteScroll.ts`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseProposalVisibilityResult {
  /** Whether the sentinel element is within (or near) the viewport. */
  isVisible: boolean;
  /**
   * Callback ref to attach to the sentinel element.
   * When this element enters the extended viewport area, isVisible becomes true.
   */
  sentinelRef: (node: HTMLElement | null) => void;
}

/**
 * Hook that returns whether a card is near the viewport so the parent can
 * gate expensive data fetches behind visibility.
 *
 * @example
 * ```tsx
 * function InlineProposalCard({ proposalId, orgId }) {
 *   const { isVisible, sentinelRef } = useProposalVisibility();
 *   const { data } = useProposalOptional(orgId, isVisible ? proposalId : undefined);
 *
 *   return (
 *     <div ref={sentinelRef}>
 *       {data ? <ProposalContent proposal={data} /> : <Skeleton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useProposalVisibility(): UseProposalVisibilityResult {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    // Cleanup any previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) {
      setIsVisible(false);
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: '200px' }
    );

    observerRef.current.observe(node);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { isVisible, sentinelRef };
}
